import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Heart, Phone } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { handleAnchorClick } from "../utils/smoothScroll";
import { contentAPI } from "../services/api";
// import { useLanguage } from "../hooks/useLanguage"; // COMMENTED OUT: Simplified, no multilanguage
import { clearCache } from "../utils/apiCache";

const Hero = () => {
  // const currentLanguage = useLanguage(); // COMMENTED OUT: Simplified, no multilanguage
  const currentLanguage = 'de'; // Hardcoded to German
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const bgIntervalRef = useRef(null);

  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        // Clear cache to ensure fresh data
        clearCache('sections-home');
        const sections = await contentAPI.getSections("home").catch(() => []);
        const heroSection = sections.find(s => s.section_type === "hero");
        if (heroSection?.content_data) {
          setHeroData(heroSection.content_data);
        }
      } catch (error) {
        console.error("Error fetching hero data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHeroData();

    // Refresh data every 10 seconds to catch updates (faster for admin changes)
    const refreshInterval = setInterval(fetchHeroData, 10000);
    return () => clearInterval(refreshInterval);
  }, []);

  // Listen for storage events to refresh when admin saves
  useEffect(() => {
    const handleStorageChange = () => {
      // Refresh when admin saves (could trigger a storage event or custom event)
      const fetchHeroData = async () => {
        clearCache('sections-home');
        const sections = await contentAPI.getSections("home").catch(() => []);
        const heroSection = sections.find(s => s.section_type === "hero");
        if (heroSection?.content_data) {
          setHeroData(heroSection.content_data);
        }
      };
      fetchHeroData();
    };

    // Listen for custom event when content is saved
    window.addEventListener('contentUpdated', handleStorageChange);
    return () => window.removeEventListener('contentUpdated', handleStorageChange);
  }, []);

  // Background slideshow effect
  useEffect(() => {
    // Handle both snake_case and camelCase keys
    const backgroundImages = heroData?.background_images || heroData?.backgroundImages || [];
    if (backgroundImages.length > 1) {
      bgIntervalRef.current = setInterval(() => {
        setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
      }, 5000); // 5 seconds
      return () => {
        if (bgIntervalRef.current) {
          clearInterval(bgIntervalRef.current);
        }
      };
    } else {
      setCurrentBgIndex(0);
      if (bgIntervalRef.current) {
        clearInterval(bgIntervalRef.current);
      }
    }
  }, [heroData?.background_images]);

  // Helper to get string value - SIMPLIFIED: no i18n, just return string or first value
  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      // If it's an object, try to get the first string value (for backwards compatibility)
      const firstKey = Object.keys(value)[0];
      if (firstKey && typeof value[firstKey] === 'string') {
        return value[firstKey];
      }
      return String(value) || fallback;
    }
    return String(value) || fallback;
  };

  // Default content
  const badge = heroData ? getStringValue(heroData.badge, "Professionelle Pflege mit Herz") : "Professionelle Pflege mit Herz";
  const headline1 = heroData ? getStringValue(heroData.headline_part1 || heroData.headline, "Serafina Pflegedienst") : "Serafina Pflegedienst";
  const headline2 = heroData ? getStringValue(heroData.headline_part2, "Ihre Pflege zu Hause") : "Ihre Pflege zu Hause";
  const description = heroData ? getStringValue(heroData.description, "Professionelle häusliche Pflege und Betreuung mit Kompetenz, Herz und Respekt. Wir unterstützen Sie dabei, Ihr Leben so unabhängig wie möglich zu führen.") : "Professionelle häusliche Pflege und Betreuung mit Kompetenz, Herz und Respekt. Wir unterstützen Sie dabei, Ihr Leben so unabhängig wie möglich zu führen.";
  
  // Headline styling with responsive defaults
  const headline1FontSize = heroData?.headline_part1_font_size || "clamp(1.875rem, 4vw, 3.75rem)"; // Responsive: 30px to 60px
  const headline1Color = heroData?.headline_part1_color || "var(--vortex-text-primary)";
  const headline2FontSize = heroData?.headline_part2_font_size || "clamp(1.875rem, 4vw, 3.75rem)"; // Responsive: 30px to 60px
  const headline2Color = heroData?.headline_part2_color || "var(--vortex-accent-primary)";
  const descriptionColor = heroData?.description_color || "var(--vortex-text-secondary)";
  const ctaPrimary = heroData?.cta_primary ? {
    label: getStringValue(heroData.cta_primary.label, "§37.3 Beratungsbesuch anfragen"),
    href: heroData.cta_primary.href || "#kontakt"
  } : { label: "§37.3 Beratungsbesuch anfragen", href: "#kontakt" };
  const ctaSecondary = heroData?.cta_secondary ? {
    label: getStringValue(heroData.cta_secondary.label, "02133-21 84 111"),
    href: heroData.cta_secondary.href || "tel:+4921332184111"
  } : { label: "02133-21 84 111", href: "tel:+4921332184111" };
  
  // Hero image settings
  const heroImageUrl = heroData?.hero_image_url || null;
  const heroImagePosition = heroData?.hero_image_position || "center"; // center, left, right
  const heroImageAnimation = heroData?.hero_image_animation || "none"; // none, pulsating, vibration, float, bounce
  
  // Background images - filter out empty strings and non-string values, extract from objects if needed
  // Handle both snake_case and camelCase keys
  const backgroundImagesRaw = heroData?.background_images || heroData?.backgroundImages || [];
  const backgroundImages = backgroundImagesRaw.filter(img => {
    if (!img) return false;
    if (typeof img === 'string') {
      return img.trim() !== '';
    }
    // Handle objects - might be i18n object, extract string value
    if (typeof img === 'object') {
      const strValue = getStringValue(img, '');
      return strValue.trim() !== '';
    }
    // Handle other types - convert to string if possible
    return String(img).trim() !== '';
  }).map(img => {
    // Extract string value if it's an object
    if (typeof img === 'string') {
      return img;
    }
    if (typeof img === 'object') {
      return getStringValue(img, '');
    }
    return String(img);
  });
  const currentBackgroundImage = backgroundImages.length > 0 ? backgroundImages[currentBgIndex] : null;
  
  const trustIndicatorsTitle = heroData ? getStringValue(heroData.trust_indicators_title || heroData.trustIndicatorsTitle, "Vertrauen Sie auf unsere Qualität") : "Vertrauen Sie auf unsere Qualität";
  // Handle both snake_case and camelCase keys
  const rawTrustIndicators = heroData?.trust_indicators || heroData?.trustIndicators || [
    { text: "MDK zertifiziert", icon: "Heart" },
    { text: "Qualifizierte Pflegefachkräfte", icon: "Heart" },
    { text: "Langjährige Erfahrung", icon: "Heart" }
  ];
  // Process trust indicators to extract string values from i18n objects
  const trustIndicators = rawTrustIndicators.map(indicator => {
    let iconName = "Heart";
    if (indicator.icon) {
      if (typeof indicator.icon === 'string') {
        iconName = indicator.icon;
      } else if (typeof indicator.icon === 'object') {
        iconName = getStringValue(indicator.icon, "Heart");
      } else {
        iconName = String(indicator.icon) || "Heart";
      }
    }
    return {
      ...indicator,
      icon: iconName, // Ensure icon is always a string
      text: getStringValue(indicator.text)
    };
  });

  // Animation classes
  const getAnimationClass = () => {
    switch (heroImageAnimation) {
      case "pulsating":
        return "animate-pulse";
      case "vibration":
        return "animate-vibrate";
      case "float":
        return "animate-float";
      case "bounce":
        return "animate-bounce";
      default:
        return "";
    }
  };

  // Layout classes based on image position
  const getLayoutClasses = () => {
    if (heroImagePosition === "left") {
      return "flex-row";
    } else if (heroImagePosition === "right") {
      return "flex-row";
    }
    return "flex-col";
  };

  // Don't render content until data is loaded to prevent flash of default content
  if (loading || !heroData) {
    return (
      <section 
        id="hero" 
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-12 sm:pt-28 sm:pb-16"
      >
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[var(--vortex-bg)] to-[var(--vortex-surface)]"></div>
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 w-full flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--vortex-accent-primary)] border-r-transparent"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-8 sm:pt-28 sm:pb-12 md:pb-16"
    >
      {/* Background images with fade transition */}
      {backgroundImages.length > 0 ? (
        backgroundImages.map((bgUrl, index) => {
          // Ensure bgUrl is a string
          const imageUrl = typeof bgUrl === 'string' ? bgUrl : (typeof bgUrl === 'object' ? getStringValue(bgUrl, '') : String(bgUrl || ''));
          if (!imageUrl || imageUrl.trim() === '') {
            return null;
          }
          return (
            <div
              key={index}
              className={`absolute inset-0 z-0 transition-opacity duration-1000 ease-in-out ${
                index === currentBgIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
          );
        }).filter(Boolean)
      ) : (
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(to bottom, var(--vortex-bg), var(--vortex-surface))'
          }}
        />
      )}
      
      {/* Background overlay for readability */}
      {(() => {
        const overlayOpacity = heroData?.background_overlay_opacity !== undefined 
          ? parseFloat(heroData.background_overlay_opacity) 
          : 0.5; // Default 50% opacity
        const opacityValue = Math.max(0, Math.min(1, overlayOpacity)); // Clamp between 0 and 1
        return (
          <div 
            className="absolute inset-0 z-[1]"
            style={{
              background: `linear-gradient(to bottom, rgba(0, 0, 0, ${opacityValue}), rgba(0, 0, 0, ${opacityValue * 0.7}))`
            }}
          ></div>
        );
      })()}
      
      <div className={`relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 w-full min-w-0 ${
        heroImagePosition !== "center" && heroImageUrl 
          ? "flex flex-col lg:" + getLayoutClasses() + " items-center gap-4 sm:gap-6 md:gap-8 lg:gap-12" 
          : ""
      }`}>
        {/* Hero Image - Happy Elderly People (Left position - show first) */}
        {heroImageUrl && heroImagePosition === "left" && (
          <div className="relative z-[2] flex-1 max-w-lg w-full order-1 lg:order-none">
            <div className={`relative w-full h-40 sm:h-56 md:h-72 lg:h-[500px] rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden border-2 sm:border-3 md:border-4 border-[var(--vortex-border)] shadow-lg sm:shadow-xl md:shadow-2xl ${getAnimationClass()}`}>
              <img 
                src={heroImageUrl} 
                alt="Glückliche ältere Menschen" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        <div className={`${
          heroImagePosition === "center" ? "text-center" : 
          heroImagePosition === "left" ? "text-center lg:text-right" : 
          "text-center lg:text-left"
        } space-y-4 sm:space-y-6 lg:space-y-8 ${
          heroImagePosition !== "center" && heroImageUrl ? "flex-1 order-2 lg:order-none" : "w-full"
        }`}>
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-[var(--vortex-surface-alt)] px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 rounded-full border-2 border-[var(--vortex-border)]">
            <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-[var(--vortex-accent-primary)]" />
            <span className="text-xs sm:text-sm md:text-base font-medium text-[var(--vortex-text-primary)]">
              {badge}
            </span>
          </div>

          {/* Main Headline */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
            <h1 className="font-bold leading-tight">
              <span 
                className="block"
                style={{
                  fontSize: headline1FontSize,
                  color: headline1Color
                }}
              >
                {headline1}
              </span>
              <span 
                className="block"
                style={{
                  fontSize: headline2FontSize,
                  color: headline2Color
                }}
              >
                {headline2}
              </span>
            </h1>
            <p 
              className={`text-base sm:text-lg md:text-xl lg:text-2xl ${heroImagePosition === "center" ? "max-w-3xl mx-auto" : ""} leading-relaxed px-1 sm:px-2 md:px-0`}
              style={{ color: descriptionColor }}
            >
              {description}
            </p>
          </div>

          {/* CTAs */}
          <div className={`flex flex-col sm:flex-row gap-3 sm:gap-4 ${heroImagePosition === "center" ? "justify-center" : heroImagePosition === "left" ? "justify-center lg:justify-end" : "justify-center lg:justify-start"} items-stretch sm:items-center w-full sm:w-auto px-2 sm:px-0`}>
            <a
              href="#kontakt"
              onClick={(e) => {
                handleAnchorClick(e, 'kontakt');
              }}
              className="btn-primary px-5 py-3 sm:px-6 md:px-8 sm:py-3 md:py-4 text-base sm:text-lg font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {ctaPrimary.label}
            </a>
            <a
              href={ctaSecondary.href}
              className="btn-primary px-5 py-3 sm:px-6 md:px-8 sm:py-3 md:py-4 text-base sm:text-lg font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              style={{ backgroundColor: '#fff7e6', borderColor: '#fff7e6' }}
              aria-label={`Rufen Sie uns an: ${ctaSecondary.label}`}
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              {ctaSecondary.label}
            </a>
          </div>

          {/* Trust Indicators */}
          {trustIndicators.length > 0 && (
            <div className="pt-6 sm:pt-8 space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm text-white uppercase tracking-wider font-semibold">
                {trustIndicatorsTitle}
              </p>
              <div className={`flex flex-wrap ${heroImagePosition === "center" ? "items-center justify-center" : heroImagePosition === "left" ? "items-center justify-center lg:justify-end" : "items-center justify-center lg:justify-start"} gap-3 sm:gap-4 lg:gap-6`}>
                {trustIndicators.map((indicator, index) => {
                  // Icon is already processed and guaranteed to be a string
                  const iconName = indicator.icon || "Heart";
                  
                  // Get icon from LucideIcons - handle both function and object components
                  const lucideKeys = Object.keys(LucideIcons);
                  const exactMatch = LucideIcons[iconName];
                  const caseInsensitiveKey = lucideKeys.find(k => k.toLowerCase() === iconName.toLowerCase());
                  const caseInsensitiveMatch = caseInsensitiveKey ? LucideIcons[caseInsensitiveKey] : null;
                  
                  // For lucide-react, if the key exists and the value is truthy, it's a valid component
                  // Production builds wrap components as objects, but they're still usable
                  const isLucideComponent = (comp) => {
                    return comp !== null && comp !== undefined && (typeof comp === 'function' || typeof comp === 'object');
                  };
                  
                  // Try to get icon - check exact match first, then case-insensitive
                  let IconComponent = Heart;
                  if (exactMatch && isLucideComponent(exactMatch)) {
                    IconComponent = exactMatch;
                  } else if (caseInsensitiveMatch && isLucideComponent(caseInsensitiveMatch)) {
                    IconComponent = caseInsensitiveMatch;
                  }
                  return (
                    <div key={index} className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#C9A15A]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border-2 border-[#6B4A2D]/50 shadow-lg">
                        <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <span className="text-sm sm:text-base font-medium text-white">{indicator.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Hero Image - Happy Elderly People (Center or Right position) */}
        {heroImageUrl && heroImagePosition !== "left" && (
          <div className={`relative z-[2] ${
            heroImagePosition === "center" 
              ? "max-w-4xl mx-auto mt-6 sm:mt-8 md:mt-12 w-full" 
              : "flex-1 max-w-lg w-full order-3 lg:order-none"
          }`}>
            <div className={`relative w-full ${
              heroImagePosition === "center" 
                ? "h-40 sm:h-56 md:h-72 lg:h-96" 
                : "h-40 sm:h-56 md:h-72 lg:h-[500px]"
            } rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden border-2 sm:border-3 md:border-4 border-[var(--vortex-border)] shadow-lg sm:shadow-xl md:shadow-2xl ${getAnimationClass()}`}>
              <img 
                src={heroImageUrl} 
                alt="Glückliche ältere Menschen" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24 lg:h-32 bg-gradient-to-t from-[var(--vortex-surface)]/90 to-transparent z-[1]"></div>
    </section>
  );
};

export default Hero;
